<template>
    <div :class="namespaceClass('-view')">
        <mds-layout-grid gutters="none">
            <mds-row v-if="includeRebalance" :class="namespaceClass('-view__main')">
                <mds-col>
                    <mds-form>
                        <mds-fieldset v-if="!isSimpleAssumptionWorkflow" variation="radio-group" :legend="getUILabel('rebalancing_rebalance_to')">
                            <mds-radio-button
                                v-for="(option, index) in rebalanceOptions"
                                :e2e="`rebalancing-to-${index}`"
                                name="rebalanceOptions"
                                :value="option.value"
                                v-model="rebalanceType"
                                :disabled="isSimpleAssumptionWorkflow && option.value !== 0"
                                :key="option.value"
                                >{{ getUILabel(option.labelId) }}</mds-radio-button
                            >
                        </mds-fieldset>
                        <mds-fieldset variation="radio-group" :legend="getUILabel('rebalancing_frequency')">
                            <mds-radio-button
                                v-for="(option, index) in frequencyOptions"
                                :e2e="`frequency-${index}`"
                                name="frequencyOptions"
                                :value="option.value"
                                v-model="frequencyType"
                                :disabled="false"
                                :key="option.value"
                                >{{ getUILabel(option.labelId) }}</mds-radio-button
                            >
                        </mds-fieldset>
                        <mds-input
                            e2e="rebalancing-threshold"
                            :label="getUILabel('rebalancing_threshold')"
                            :value="weightFormatter(threshold)"
                            size="small"
                            @change="handleThresholdInput"
                        ></mds-input>
                    </mds-form>
                    <div :class="namespaceClass('-view__rebalancing-table-label')">
                        Holdings
                    </div>
                    <mds-row>
                        <mds-col :class="namespaceClass('-view__main-table_wrapper')">
                            <mds-data-table class="rebalance-table" size="small" :header-configs="headers" :row-data="rebalanceSecurities">
                                <template v-slot:header>
                                    <thead class="table-header">
                                        <tr>
                                            <th scope="col" class="table-column">{{ getUILabel("rebalancing_name") }}</th>
                                            <th scope="col" class="table-column">{{ getUILabel("rebalancing_id") }}</th>
                                            <th scope="col" class="table-column">
                                                {{ getUILabel("rebalancing_asset_breakdown") }}
                                            </th>
                                            <th scope="col" class="table-column right">{{ getUILabel("rebalancing_initial_weight") }}</th>
                                            <th scope="col" style="width:12%;" class="table-column right" v-if="showCustomWeightCol">
                                                {{ getUILabel("rebalancing_custom_weight") }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <thead class="table-header-total">
                                        <tr>
                                            <th scope="col" class="table-column">Total</th>
                                            <th scope="col" class="table-column"></th>
                                            <th scope="col" class="table-column"></th>
                                            <th scope="col" class="table-column right">100.00</th>
                                            <th scope="col" :class="['table-column', 'right', errorClass]" v-if="showCustomWeightCol">
                                                {{ customWeightTotal.toFixed(2) }}
                                            </th>
                                        </tr>
                                    </thead>
                                </template>
                                <template v-slot:body>
                                    <mds-data-table-body :header-configs="headers" :row-data="rebalanceSecurities">
                                        <template v-slot:body-cell="cellData">
                                            <template v-if="cellData.colIndex === 2">
                                                <mwc-asset-allocation-bar
                                                    v-if="isValid(cellData.rowData.assetAllocation)"
                                                    :mwcId.prop="'asset-allocation-bar-' + cellData.rowIndex"
                                                    :class="namespaceClass('-view__asset-allocation')"
                                                    :assets.prop="JSON.stringify(cellData.rowData.assetAllocation)"
                                                    popover-position="bottom-center"
                                                ></mwc-asset-allocation-bar>
                                                <div v-else :class="namespaceClass('-view__asset-allocation-placeholder')"></div>
                                            </template>

                                            <template v-else-if="cellData.colIndex === 4">
                                                <mds-input
                                                    v-if="showCustomWeightCol"
                                                    :e2e="`custom-rebalance-${cellData.rowIndex}`"
                                                    :label="getUILabel('rebalancing_custom_weight')"
                                                    hidden-label
                                                    right-aligned
                                                    size="small"
                                                    :value="weightFormatter(cellData.rowData.customWeight)"
                                                    @change="handleCustomWeightChanged($event, cellData.rowData)"
                                                >
                                                </mds-input>
                                            </template>
                                        </template>
                                    </mds-data-table-body>
                                </template>
                            </mds-data-table>
                        </mds-col>
                    </mds-row>
                </mds-col>
            </mds-row>
        </mds-layout-grid>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import { mapState } from "vuex";
import * as types from "../store/mutation-types";
import { roundNumber } from "@/util";
import "mwc-asset-allocation-bar";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import MdsForm from "@mds/form";
import MdsFieldset from "@mds/fieldset";
import MdsInput from "@mds/input";
import MdsRadioButton from "@mds/radio-button";
import { MdsDataTable, MdsDataTableBody } from "@mds/data-table";
import { MdsLayoutGrid, MdsRow, MdsCol } from "@mds/layout-grid";

export default {
    name: "rebalancing-content",
    components: {
        MdsForm,
        MdsFieldset,
        MdsInput,
        MdsRadioButton,
        MdsDataTable,
        MdsDataTableBody,
        MdsRow,
        MdsCol,
        MdsLayoutGrid
    },
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    data() {
        return {
            defaultConfig: {
                labels: this.$store.state.labels.labels
            },
            errorClass: "",
            tableHeaders: [
                {
                    fieldName: "securityName"
                },
                {
                    fieldName: "identifier"
                },
                {
                    fieldName: "assetAllocation:Array"
                },
                {
                    fieldName: "initialWeight",
                    align: "right",
                    transform: value => {
                        return value.toFixed(2);
                    }
                },
                {
                    fieldName: "customWeight",
                    align: "right",
                    transform: value => {
                        return value.toFixed(2);
                    }
                }
            ]
        };
    },
    computed: {
        ...mapState("rebalancing", ["includeRebalance", "threshold"]),
        frequencyOptions() {
            return this.convertOptionLabelIdToWords(this.$store.state.rebalancing.frequencyOptions);
        },
        isSimpleAssumptionWorkflow() {
            return this.$store.getters["settings/isSimpleAssumptionWorkflow"];
        },
        rebalanceOptions() {
            return this.convertOptionLabelIdToWords(this.$store.state.rebalancing.rebalanceOptions);
        },
        rebalanceType: {
            get() {
                return this.$store.state.rebalancing.rebalanceType;
            },
            set(value) {
                return this.$store.commit(`rebalancing/${types.SET_REBALANCE_TYPE}`, parseInt(value));
            }
        },
        frequencyType: {
            get() {
                return this.$store.state.rebalancing.frequencyType;
            },
            set(value) {
                return this.$store.commit(`rebalancing/${types.SET_FREQUENCY_TYPE}`, parseInt(value));
            }
        },
        showCustomWeightCol() {
            return this.rebalanceType !== 0; //0: initial allocation, 1:custom
        },
        headers() {
            return !this.showCustomWeightCol ? this.tableHeaders.filter(item => item.fieldName !== "customWeight") : this.tableHeaders;
        },
        rebalanceSecurities() {
            const securities = this.$store.getters["rebalancing/getRebalanceSecurities"];
            return securities.map((security, index) => {
                return {
                    id: index,
                    ...security
                };
            });
        },
        customWeightTotal() {
            return this.rebalanceSecurities.length === 0 ? 0 : this.rebalanceSecurities.reduce((total, sec) => total + (sec.customWeight || 0), 0);
        }
    },
    watch: {
        rebalanceType: {
            handler(newVal) {
                if (newVal === 0) {
                    this.$emit("custom-rebalancing-error", {
                        key: "rebalance",
                        payload: null
                    });
                } else {
                    this.checkRebalanceSecurities(this.rebalanceSecurities);
                }
            }
        },
        includeRebalance: {
            handler(newVal) {
                if (newVal) {
                    this.checkRebalanceSecurities(this.rebalanceSecurities);
                } else {
                    this.$emit("custom-rebalancing-error", {
                        key: "rebalance",
                        payload: null
                    });
                }
            }
        }
    },
    mounted() {
        if (this.rebalanceType !== 0) {
            this.checkRebalanceSecurities(this.rebalanceSecurities);
        }
    },
    methods: {
        weightFormatter(value) {
            return this.formatter.number(value, "shortDecimal");
        },
        isValid(data) {
            if (typeof data === "undefined" || data.length === 0) {
                return false;
            }
            if (data.some(i => i.weight < 0)) {
                return false;
            }
            return true;
        },
        handleThresholdInput(e) {
            if (/\d+/.test(e.target.value)) {
                const threshold = parseFloat(e.target.value);
                if (threshold > 100 || threshold < 0) {
                    e.target.value = this.weightFormatter(this.threshold);
                } else {
                    this.$store.commit(`rebalancing/${types.SET_THRESHOLD}`, roundNumber(threshold));
                }
            } else {
                e.target.value = this.weightFormatter(this.threshold);
            }
        },
        validateCustomRebalancing(securities) {
            const total = securities.reduce((prev, current) => {
                return { customWeight: prev.customWeight + current.customWeight };
            });
            return roundNumber(parseFloat(total.customWeight)) !== 100;
        },
        checkRebalanceSecurities(newRebalanceSecurities) {
            if (this.rebalanceType !== 0) {
                const error = {
                    key: "rebalance",
                    payload: null
                };
                error.payload = this.validateCustomRebalancing(newRebalanceSecurities)
                    ? {
                          title: this.getUILabel("alert_rebalance_total_title"),
                          text: this.getUILabel("alert_rebalance_total_msg")
                      }
                    : null;
                this.errorClass = error.payload ? "custom-weight-error" : "";
                this.$emit("custom-rebalancing-error", error);
            }
        },
        handleCustomWeightChanged(e, rowData) {
            const rawWeight = rowData.customWeight;
            if (/\d+/.test(e.target.value)) {
                const currentValue = roundNumber(parseFloat(e.target.value));
                if (currentValue > 100 || currentValue < 0) {
                    e.target.value = this.weightFormatter(rawWeight);
                } else {
                    const newRebalanceSecurities = this.rebalanceSecurities.map(item => {
                        if (item.securityId === rowData.securityId) {
                            return {
                                ...item,
                                customWeight: currentValue
                            };
                        } else {
                            return item;
                        }
                    });

                    this.checkRebalanceSecurities(newRebalanceSecurities);
                    this.$store.commit(`rebalancing/${types.SET_REBALANCE_SECURITIES}`, newRebalanceSecurities);
                }
            } else {
                e.target.value = this.weightFormatter(rawWeight);
            }
        },
        convertOptionLabelIdToWords(rawOptions) {
            return rawOptions.map(option => {
                return {
                    ...option,
                    text: this.getUILabel(option.labelId)
                };
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/constants";
@import "~@mds/typography";
$el: "rebalancing-content-view";
.#{$el} {
    &__main {
        &-settings {
            border: 1px solid #cccccc;
            border-radius: 2px;
            padding: $mds-space-2-x;
            margin-bottom: $mds-space-2-x;

            &-title {
                @include mds-body-text-s($bold: true);
                color: $mds-text-color-primary;
                letter-spacing: 1.67px;
                text-transform: uppercase;
                margin-bottom: $mds-space-1-and-a-half-x;
            }

            .mds-form__field-group {
                margin: 0px;
            }

            .mds-form__field-group--horizontal > .mds-form__field-group {
                flex: 1;
                margin-right: $mds-space-2-x;
                &:last-child {
                    margin-right: 0px;
                }
                /deep/ .mds-form__select {
                    display: block;
                }
            }
        }
        /deep/ mwc-asset-allocation-bar.#{$el}__asset-allocation {
            > div {
                position: relative;
                height: 10px;
                line-height: 10px;
            }
            .allocation-display {
                width: 100%;
                white-space: nowrap;
            }
            .mds-popover__wrapper {
                display: none;
                &.mds-popover__wrapper--visible {
                    display: inline-block;
                }
            }
            .mds-data-table {
                line-height: 1.25;
                .mds-data-table__row:first-child .mds-data-table__cell {
                    border-top: 0;
                }
                .mds-data-table__row .mds-data-table__cell {
                    border-top: $mds-border-separator;
                }
            }
        }
    }

    &__rebalancing-table-label {
        border-top: solid 1px #e5e5e5;
        color: #1e1e1e;
        font-size: 16px;
        line-height: 20px;
        margin: 25px 0 15px 0;
        width: 100%;
    }

    &__asset-allocation-placeholder {
        background-color: #e5e5e5;
        width: 100%;
        height: 10px;
    }
}
.table-column {
    padding: 4px 8px;
    text-align: left;
    &:first-child {
        padding-left: 0;
    }
    &:last-child {
        padding-right: 0;
    }
    &.right {
        text-align: right;
    }
    &.custom-weight-error {
        color: $mds-text-color-error;
    }
}
.table-header {
    font-size: 14px;
    line-height: 18px;
    color: $mds-text-color-primary;
}
.table-header-total {
    color: #1e1e1e;
    background-color: transparent;
    font-size: 16px;
    line-height: 22px;
}
</style>
<style lang="scss">
// MDS overrides only
.mds-data-table__cell___hypo {
    overflow: visible;
}
</style>
